import type { ReactNode } from "react";

/**
 * § Feedback and status: the in-place notice `RateLimitNotice` draws, and the
 * shape of the page-top strip `OfflineBanner` and `ConnectionBanner` draw, which `Banner.tsx`
 * beside this file holds (2026-09-28: a module of its own, so the public entry does not carry it).
 * The notice lives here because several components share it, and the class strings are exported
 * for a screen that composes a notice of its own words (the refused state change, the transition
 * graph's warnings).
 *
 * Taken from Spectrum UI's Animated Alert (`@spectrumui/alert-1`, Apache-2.0) over shadcn/ui's Alert
 * (new-york-v4, MIT) and themed through the variables of `packages/tokens` (2026-09-18, slice (f)):
 * the panel at the large radius with 16 px of padding, tinted and bordered in its accent, the row of
 * a leading tile, the words and the actions, the panel's fade down into place and the tile's turn
 * upright from a tilt and half size. Both are written with the `animate-in` utilities and the
 * duration tokens rather than with Motion (2026-09-21, T292): the public pages draw this notice and
 * the engine it imported cost the public chunk 41 KB gzipped against a 135 KB budget. Reduced
 * motion, from the system or from `data-motion="reduced"`, resolves those tokens to zero and shows
 * both at rest.
 *
 * What differs from the source, and why:
 * - the tint and the border are the accent's semantic tokens (amber for a caution, red for a refusal,
 *   the agent purple when an agent made the change) where the source writes a violet gradient, because
 *   a notice's colour says what kind it is and the theme has one palette;
 * - the tile turns upright on the slow duration token's ease-out where the source springs it, because
 *   the spring was Motion's and the engine is not loaded here; the tilt and the half size it grows
 *   from are the source's;
 * - the confetti blobs, the corner badge and the staggered slide of the title and body are not
 *   carried: they celebrate a milestone, and these notices report a conflict or a pause;
 * - the panel's fade down runs on a 250 ms ease-out rather than Motion's default, which springs a
 *   transform, because a notice that arrives inside a form or over a list should settle without
 *   passing its place;
 * - the actions wrap under the words when the host is narrow, where the source has no actions;
 * - the strip is the owner's decision of 2026-09-18 (a hard red strip across the page's top, its
 *   words centred), so it takes the source's fade down and leading tile and not its panel; one line of it is the medium control height whether or not it carries the small Retry action, so a banner with an action is exactly as tall as one without (2026-09-23, by the owner's decision: the strip grew when it held a button), and a message longer than the strip is wide wraps onto more lines and the strip grows, its action dropping under the words (2026-10-24, by the owner's decision: one fixed height cut a long message at both ends).
 */
export type NoticeAccent = "amber" | "red" | "purple";

export const noticeClassName =
  "flex flex-wrap items-center gap-3 rounded-xl border border-accent-amber-solid bg-accent-amber-bg p-4 text-small text-text-primary data-[accent=purple]:border-agent data-[accent=purple]:bg-accent-purple-bg data-[accent=red]:border-accent-red-solid data-[accent=red]:bg-accent-red-bg";
// The words grow from 16 rem, so on a narrow host the actions wrap under them rather than squeezing them into a column.
export const noticeTextClassName = "m-0 min-w-0 flex-[1_1_16rem]";
export const noticeActionsClassName = "flex gap-1";


export const enterClassName = "animate-in fade-in-0 slide-in-from-top-2 duration-(--duration-slow) ease-(--ease-out) motion-reduce:animate-none";
const tileEnterClassName = "animate-in zoom-in-50 spin-in-[-15deg] duration-(--duration-slow) ease-(--ease-out) motion-reduce:animate-none";

/** The leading tile: an avatar or a glyph that springs upright as the notice arrives. */
export function NoticeTile({ children }: { readonly children: ReactNode }) {
  return (
    <span data-slot="notice-tile" className={`inline-flex flex-none items-center [&>svg]:size-(--icon-sm) ${tileEnterClassName}`}>
      {children}
    </span>
  );
}

export interface NoticeProps {
  readonly accent?: NoticeAccent;
  readonly role: "alert" | "status";
  readonly tile?: ReactNode;
  readonly actions?: ReactNode;
  readonly expired?: boolean;
  readonly children: ReactNode;
}

export function Notice({ accent = "amber", role, tile, actions, expired = false, children }: NoticeProps) {
  return (
    <div data-slot="notice" data-accent={accent} data-expired={expired ? "true" : undefined} role={role} className={`${noticeClassName} ${enterClassName}`}>
      {tile !== undefined && <NoticeTile>{tile}</NoticeTile>}
      <span className={noticeTextClassName}>{children}</span>
      {actions !== undefined && <span className={noticeActionsClassName}>{actions}</span>}
    </div>
  );
}
